import { useTranslation } from 'react-i18next'
import { ArrowRightLeft, ArrowRight, ArrowLeft, Webhook, KeyRound, BadgeCheck, BookOpen, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

function MethodBadge({ method }: { method: string }) {
  const map: Record<string, string> = {
    GET: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    POST: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    PATCH: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
    DELETE: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20',
  }
  return (
    <Badge variant="outline" className={`font-mono text-[10px] ${map[method] ?? map.GET}`}>
      {method}
    </Badge>
  )
}

function DirectionBadge({ direction }: { direction: string }) {
  const { t } = useTranslation()
  const map: Record<string, string> = {
    outbound: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20',
    inbound: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20',
    direct: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20',
  }
  return (
    <Badge variant="outline" className={`text-[10px] ${map[direction] ?? map.direct}`}>
      {t(`iacrm.docs.directions.${direction}`)}
    </Badge>
  )
}

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation()
  const map: Record<string, string> = {
    backend: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20',
    frontend: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20',
  }
  return (
    <Badge variant="outline" className={`text-[10px] ${map[role] ?? map.frontend}`}>
      {t(`iacrm.docs.roles.${role}`)}
    </Badge>
  )
}

function Code({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div className="mt-3 overflow-hidden rounded-lg bg-muted/50">
      {title ? (
        <div className="flex items-center gap-1.5 border-b border-border/50 px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase">
          <BadgeCheck className="size-3 text-muted-foreground/60" />
          {title}
        </div>
      ) : null}
      <pre className="overflow-x-auto p-3 text-[11px] leading-relaxed text-foreground">{children}</pre>
    </div>
  )
}

function FlowStep({ number, title, subtext }: { number: number; title: string; subtext: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
        {number}
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{subtext}</p>
      </div>
    </div>
  )
}

// ─── Real endpoint metadata ────────────────────────────────────────────────

const endpointsMeta = [
  {
    id: 'auth-token',
    method: 'POST',
    path: '/auth/token',
    direction: 'direct',
    role: 'frontend',
    dataIn: 'None (auth header only)',
    dataOut: '{ token: string, expires_at: string }',
    curl: `curl -X POST https://iacrm-api.example.com/auth/token \\
  -H "Content-Type: application/json" \\
  -H "X-IACRM-API-Key: <api_key>"`,
  },
  {
    id: 'businesses-list',
    method: 'GET',
    path: '{businesses_url}',
    direction: 'direct',
    role: 'frontend',
    dataIn: 'None (auth header only)',
    dataOut: '[{ iacrm_id, display_name, legal_name, industry, country_code, status, created_at, services_count, clients_count, pipeline_count }]',
    curl: `curl https://iacrm-api.example.com/businesses \\
  -H "X-IACRM-API-Key: <api_key>"`,
  },
  {
    id: 'services-list',
    method: 'GET',
    path: '/services',
    direction: 'direct',
    role: 'frontend',
    dataIn: 'None (auth header only)',
    dataOut: '[{ id, name, description, price, currency, duration, category }]',
    curl: `curl https://iacrm-api.example.com/services \\
  -H "X-IACRM-API-Key: <api_key>"`,
  },
  {
    id: 'clients-list',
    method: 'GET',
    path: '/clients',
    direction: 'direct',
    role: 'frontend',
    dataIn: 'None (auth header only)',
    dataOut: '[{ id, contact_name, contact_email, contact_phone, company_name, status, created_at }]',
    curl: `curl https://iacrm-api.example.com/clients \\
  -H "X-IACRM-API-Key: <api_key>"`,
  },
  {
    id: 'push-prospect',
    method: 'POST',
    path: '/prospects',
    direction: 'outbound',
    role: 'backend',
    dataIn: `{\n  "company_id": "<business.iacrm_business_id>",\n  "external_id": "parrainage_<prospect.id>",\n  "agent_fullname": "string",\n  "agent_id": "string",\n  "agent_code": "string",\n  "program_name": "string",\n  "program_id": "string",\n  "program_code": "string",\n  "prospect_id": "string",\n  "prospect_code": "string",\n  "business_id": "string",\n  "business_code": "string",\n  "contact_name": "string",\n  "contact_email": "string",\n  "contact_phone": "string",\n  "company_name": "string",\n  "submission_channel": "string",\n  "submitted_at": "ISO-8601",\n  "source": "hd_parrainage"\n}`,
    dataOut: '{ id: string /* iacrm_prospect_id */ }',
    curl: `curl -X POST https://iacrm-api.example.com/prospects \\
  -H "Content-Type: application/json" \\
  -H "X-IACRM-API-Key: <api_key>" \\
  -d '{\n    "company_id": "12345",\n    "external_id": "parrainage_550e8400-e29b-41d4-a716-446655440000",\n    "agent_fullname": "Jane Doe",\n    "agent_id": "agent_001",\n    "agent_code": "AG001",\n    "program_name": "Premium Surf",\n    "program_id": "prog_001",\n    "program_code": "PS001",\n    "prospect_id": "prosp_001",\n    "prospect_code": "P001",\n    "business_id": "biz_001",\n    "business_code": "B001",\n    "contact_name": "Jane Doe",\n    "contact_email": "jane@example.com",\n    "contact_phone": "+212600000000",\n    "company_name": "Lalla Surf",\n    "submission_channel": "web",\n    "submitted_at": "2026-05-15T09:00:00Z",\n    "source": "hd_parrainage"\n  }'`,
  },
  {
    id: 'push-archive',
    method: 'PATCH',
    path: '/pipeline/prospects/{iacrmId}/stage',
    direction: 'outbound',
    role: 'backend',
    dataIn: '{ stage: "lost", reason: "Archived in HD Parrainage" }',
    dataOut: '{ success: boolean }',
    curl: `curl -X PATCH https://iacrm-api.example.com/pipeline/prospects/130/stage \\
  -H "Content-Type: application/json" \\
  -H "X-IACRM-API-Key: <api_key>" \\
  -d '{\n    "stage": "lost",\n    "reason": "Archived in HD Parrainage"\n  }'`,
  },
  {
    id: 'callback-stage',
    method: 'PATCH',
    path: '/api/public/iacrm/prospects/{id}/stage',
    direction: 'inbound',
    role: 'backend',
    dataIn: `{\n  "stage": "converted",\n  "invoice": {\n    "iacrm_id": "string",\n    "reference": "string",\n    "product_name": "string",\n    "amount": 0,\n    "status": "paid | unpaid | pending | overdue | cancelled",\n    "issued_at": "ISO-8601"\n  }\n}`,
    dataOut: `{\n  "message": "Prospect stage updated successfully.",\n  "data": {\n    "prospect_id": "uuid",\n    "iacrm_prospect_id": "130",\n    "pipeline_stage": "converted",\n    "progression_status": "converted",\n    "conversion_status": "converted",\n    "last_synced_at": "2026-05-15T09:00:00Z",\n    "transaction": {\n      "transaction_id": "uuid",\n      "iacrm_transaction_id": "string",\n      "amount": 1200,\n      "status": "paid",\n      "invoice_status": "paid",\n      "paid_at": "2026-05-15T09:00:00Z"\n    }\n  }\n}`,
    curl: `curl -X PATCH https://parrainage.example.com/api/public/iacrm/prospects/130/stage \\
  -H "Content-Type: application/json" \\
  -H "X-IACRM-API-Key: <api_key>" \\
  -d '{\n    "stage": "converted",\n    "invoice": {\n      "iacrm_id": "inv_001",\n      "reference": "INV-2026-001",\n      "product_name": "Premium Surf Package",\n      "amount": 1200,\n      "status": "paid",\n      "issued_at": "2026-05-15T09:00:00Z"\n    }\n  }'`,
  },
]

export function IacrmDocsTab() {
  const { t } = useTranslation()
  const items = t('iacrm.docs.endpoints.items', { returnObjects: true }) as Array<{
    when: string
    notes: string
  }>

  return (
    <div className="space-y-8">
      {/* Overview */}
      <section>
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <BookOpen className="size-4 text-muted-foreground" />
          {t('iacrm.docs.overview.title')}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {t('iacrm.docs.overview.description')}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge variant="outline" className="gap-1.5 bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20">
            <ArrowRightLeft className="size-3" />
            {t('iacrm.docs.directions.direct')}
          </Badge>
          <Badge variant="outline" className="gap-1.5 bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20">
            <ArrowRight className="size-3" />
            {t('iacrm.docs.directions.outbound')}
          </Badge>
          <Badge variant="outline" className="gap-1.5 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20">
            <ArrowLeft className="size-3" />
            {t('iacrm.docs.directions.inbound')}
          </Badge>
        </div>
      </section>

      {/* Auth */}
      <section className="rounded-xl bg-card p-5">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <KeyRound className="size-4 text-muted-foreground" />
          {t('iacrm.docs.auth.title')}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {t('iacrm.docs.auth.description')}
        </p>
        <Code title="Header">X-IACRM-API-Key: your_api_key_here</Code>
      </section>

      {/* Flow */}
      <section>
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Webhook className="size-4 text-muted-foreground" />
          {t('iacrm.docs.flows.title')}
        </h3>
        <div className="mt-4 space-y-4">
          <FlowStep
            number={1}
            title={t('iacrm.docs.flows.step1.title')}
            subtext={t('iacrm.docs.flows.step1.subtext')}
          />
          <FlowStep
            number={2}
            title={t('iacrm.docs.flows.step2.title')}
            subtext={t('iacrm.docs.flows.step2.subtext')}
          />
          <FlowStep
            number={3}
            title={t('iacrm.docs.flows.step3.title')}
            subtext={t('iacrm.docs.flows.step3.subtext')}
          />
          <FlowStep
            number={4}
            title={t('iacrm.docs.flows.step4.title')}
            subtext={t('iacrm.docs.flows.step4.subtext')}
          />
        </div>
      </section>

      {/* Endpoints accordion */}
      <section>
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <ArrowRightLeft className="size-4 text-muted-foreground" />
          {t('iacrm.docs.endpoints.title')}
        </h3>

        <Accordion type="multiple" className="mt-4 space-y-3">
          {endpointsMeta.map((endpoint, index) => (
            <AccordionItem
              key={endpoint.id}
              value={endpoint.id}
              className="rounded-xl bg-card border-0"
            >
              <AccordionTrigger className="px-5 py-3 hover:no-underline [&[data-state=open]>div>.chevron]:rotate-180">
                <div className="flex w-full items-center gap-3 text-left">
                  <MethodBadge method={endpoint.method} />
                  <span className="font-mono text-sm text-foreground">{endpoint.path}</span>
                  <span className="ml-auto flex items-center gap-1.5">
                    <DirectionBadge direction={endpoint.direction} />
                    <RoleBadge role={endpoint.role} />
                    <svg
                      className="chevron size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <div className="space-y-4">
                  {/* When triggered */}
                  {items[index] && (
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground">{t('iacrm.docs.labels.when')}</p>
                      <p className="mt-1 text-sm text-foreground">{items[index].when}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {items[index] && (
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground">{t('iacrm.docs.labels.notes')}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{items[index].notes}</p>
                    </div>
                  )}

                  {/* Data In */}
                  {endpoint.dataIn !== 'None (auth header only)' ? (
                    <Code title={t('iacrm.docs.labels.dataIn')}>{endpoint.dataIn}</Code>
                  ) : (
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground">{t('iacrm.docs.labels.dataIn')}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{t('iacrm.docs.labels.noBody')}</p>
                    </div>
                  )}

                  {/* Data Out */}
                  <Code title={t('iacrm.docs.labels.dataOut')}>{endpoint.dataOut}</Code>

                  {/* cURL */}
                  <Code title={t('iacrm.docs.labels.curl')}>{endpoint.curl}</Code>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Stages */}
      <section className="rounded-xl bg-card p-5">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <BadgeCheck className="size-4 text-muted-foreground" />
          {t('iacrm.docs.stages.title')}
        </h3>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {(['suspect', 'prospect_froid', 'prospect_tiede', 'prospect_chaud', 'converted', 'lost'] as const).map(
            (stage) => (
              <div key={stage} className="flex items-center gap-2.5 rounded-lg bg-muted/40 px-3 py-2">
                <div className="size-2 shrink-0 rounded-full bg-primary/60" />
                <span className="text-sm font-medium text-foreground">
                  {t(`iacrm.docs.stages.${stage}`)}
                </span>
              </div>
            ),
          )}
        </div>
      </section>

      {/* Errors */}
      <section className="rounded-xl bg-card p-5">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <AlertTriangle className="size-4 text-muted-foreground" />
          {t('iacrm.docs.errors.title')}
        </h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>{t('iacrm.docs.errors.description')}</p>
          <ul className="list-inside list-disc space-y-1">
            {(['401', '404', '422', '429', '500'] as const).map((code) => (
              <li key={code}>{t(`iacrm.docs.errors.${code}`)}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}
